import { useContext, useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router-dom'
import { useAppSelector } from 'hooks/reduxHooks'
import { canShowPushPermissionPrompt, requestBrowserPushPermission } from 'api/braze'
import MasterModalContext from 'contexts/MasterModalContext'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { USER_PUSH_NOTIFICATIONS_DISMISSED_KEY } from 'constants/auth'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

const EnablePushNotificationsPromptRoutes = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/search',
  '/:regionCode/lux-plus',
  '/:regionCode/ultra-lux',
]

function usePushNotificationPermission() {
  const loggedIn = useAppSelector(selectLoggedIn)
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const isDismissed = getLocalStorage(USER_PUSH_NOTIFICATIONS_DISMISSED_KEY)
  const modalState = useContext(MasterModalContext)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const [canShow, setCanShow] = useState(false)
  const isMobileDevice = useIsMobileDevice()

  const isRouteValid = useMemo(() => (
    matchPath({ path: '/:regionCode', end: true }, currentPath) ||
    EnablePushNotificationsPromptRoutes.some(path => matchPath({ path, end: false }, currentPath))
  ), [currentPath])

  const isUserEnabled = loggedIn && !headlessMode && !modalState.open && !isDismissed && !!isRouteValid && !isSpoofed
  const isWebPushNotificationPromptEnabled = useFeatureFlag(OptimizelyFeatureFlags.croWebPushNotificationPrompt)

  const isPromptEnabled = isWebPushNotificationPromptEnabled && isUserEnabled
  const shouldRequestBrowserPushPermission = isPromptEnabled && canShow && config.BRAND === LUXURY_ESCAPES && !isMobileDevice

  useEffect(() => {
    const checkCanShow = async() => {
      const canShowPushPermission = await canShowPushPermissionPrompt()
      setCanShow(canShowPushPermission)
    }
    if (isPromptEnabled) {
      checkCanShow()
    }
  }, [isPromptEnabled])

  useEffect(() => {
    if (shouldRequestBrowserPushPermission) {
      requestBrowserPushPermission()
    }
  }, [shouldRequestBrowserPushPermission])
}

export default usePushNotificationPermission
